import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Container,
  Typography,
  Chip,
  Checkbox,
  ListItemText,
  Select,
  SelectChangeEvent,
  useMediaQuery,
  useTheme,
  MenuItem,
  InputLabel,
  FormControl,
  FormControlLabel,
  Switch,
  Button,
  Menu,
  CircularProgress,
} from '@mui/material';
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  writeBatch,
  Timestamp,
} from 'firebase/firestore';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/ReactToastify.css';
import { db } from '../../config/config';
import Header from '../Hub/components/Header';
import PageHeading from '../../components/PageHeading';
import StandaloneBackButton from '../../components/StandaloneBackButton';
import { filterPanel } from '../../utils/surfaces';
import GameListCard from './components/GameListCard';
import AddGameListModal, {
  NewGameListEntry,
} from './components/AddGameListModal';
import EditGameListModal from './components/EditGameListModal';
import ConfirmDeleteGameListModal from './components/ConfirmDeleteGameListModal';
import FixHltbModal from './components/FixHltbModal';
import {
  COLLECTION_NAME,
  EMPTY_MESSAGES,
  GameListEntry,
  GameState,
  GAME_LIST_PLATFORMS,
  GAME_STATES,
  HltbInfo,
  PAUSABLE_STATES,
  STATE_LABELS,
} from './types';
import { lookupHltb } from './hltb';
import { gradients } from '../../theme/colors';

const ALL_PLATFORMS = 'all';
const DEFAULT_STATES: GameState[] = ['inProgress'];
const DEFAULT_PLATFORM = 'PS5';
const RESORT_FLAG = 'gameListCompletedResort-v1';

const orderStates = (states: GameState[]) =>
  GAME_STATES.filter((state) => states.includes(state));

const finishedMillis = (game: GameListEntry) =>
  game.dateFinished ? game.dateFinished.toMillis() : -Infinity;

const GameList = () => {
  const [games, setGames] = useState<GameListEntry[]>([]);
  const [loading, setLoading] = useState(true);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [selectedStates, setSelectedStates] =
    useState<GameState[]>(DEFAULT_STATES);
  const [platformFilter, setPlatformFilter] = useState<string>(DEFAULT_PLATFORM);
  const [pausedOnly, setPausedOnly] = useState(false);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedGame, setSelectedGame] = useState<GameListEntry | null>(null);

  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showFixHltb, setShowFixHltb] = useState(false);

  useEffect(() => {
    const fetchGames = async () => {
      try {
        const snapshot = await getDocs(collection(db, COLLECTION_NAME));
        const list = snapshot.docs.map((entry) => ({
          id: entry.id,
          ...entry.data(),
        })) as GameListEntry[];

        setGames(list);
      } catch (err) {
        console.error(err);
        toast.error('Failed to load your game list');
      } finally {
        setLoading(false);
      }
    };

    fetchGames();
  }, []);

  const saveHltb = useCallback(async (gameId: string, hltb: HltbInfo) => {
    await updateDoc(doc(db, COLLECTION_NAME, gameId), { hltb });
    setGames((prev) =>
      prev.map((game) => (game.id === gameId ? { ...game, hltb } : game))
    );
  }, []);

  // Look up HLTB once for games added before the feature existed.
  const hltbBackfillStarted = useRef(false);
  useEffect(() => {
    if (loading || hltbBackfillStarted.current) return;
    hltbBackfillStarted.current = true;

    const missing = games.filter((game) => !game.hltb);
    if (missing.length === 0) return;

    (async () => {
      for (const game of missing) {
        try {
          await saveHltb(game.id, await lookupHltb(game.title, game.year));
        } catch (err) {
          // Nothing is saved for this game, so it's retried next visit.
          console.error('HLTB backfill skipped', game.title, err);
        }
      }
    })();
  }, [loading, games, saveHltb]);

  // One-off: order existing Completed games newest first. Reuses the order
  // slots they already hold so other games don't move. Remove once run.
  const resortStarted = useRef(false);
  useEffect(() => {
    if (loading || resortStarted.current) return;
    resortStarted.current = true;

    try {
      if (localStorage.getItem(RESORT_FLAG)) return;
    } catch {
      return;
    }

    const completed = games.filter((game) => game.state === 'finished');
    const slots = completed.map((game) => game.order).sort((a, b) => a - b);
    const updates = [...completed]
      .sort((a, b) => finishedMillis(b) - finishedMillis(a))
      .map((game, index) => ({ game, order: slots[index] }))
      .filter(({ game, order }) => game.order !== order);

    (async () => {
      try {
        if (updates.length > 0) {
          const batch = writeBatch(db);
          updates.forEach(({ game, order }) => {
            batch.update(doc(db, COLLECTION_NAME, game.id), { order });
          });
          await batch.commit();
          setGames((prev) =>
            prev.map((game) => {
              const update = updates.find((entry) => entry.game.id === game.id);
              return update ? { ...game, order: update.order } : game;
            })
          );
        }
        localStorage.setItem(RESORT_FLAG, '1');
      } catch (err) {
        console.error('Completed re-sort failed', err);
      }
    })();
  }, [loading, games]);

  const isDefaultFilters =
    selectedStates.length === 1 &&
    selectedStates[0] === DEFAULT_STATES[0] &&
    platformFilter === DEFAULT_PLATFORM &&
    !pausedOnly;

  const clearFilters = () => {
    setSelectedStates(DEFAULT_STATES);
    setPlatformFilter(DEFAULT_PLATFORM);
    setPausedOnly(false);
  };

  const topOrder = () =>
    games.length ? Math.min(...games.map((g) => g.order)) - 1 : 0;

  const showPausedSwitch = selectedStates.some((state) =>
    PAUSABLE_STATES.includes(state)
  );

  useEffect(() => {
    if (!showPausedSwitch) setPausedOnly(false);
  }, [showPausedSwitch]);

  // Completed on its own is a history across every platform, so it starts
  // on All platforms; the platform can still be narrowed afterwards.
  const applyStates = (next: GameState[]) => {
    if (next.length === 0) return;
    const ordered = orderStates(next);
    setSelectedStates(ordered);
    if (ordered.length === 1 && ordered[0] === 'finished') {
      setPlatformFilter(ALL_PLATFORMS);
    }
  };

  const toggleState = (state: GameState) => {
    if (!selectedStates.includes(state)) {
      applyStates([...selectedStates, state]);
    } else if (selectedStates.length > 1) {
      applyStates(selectedStates.filter((s) => s !== state));
    }
  };

  const handleStatesSelect = (event: SelectChangeEvent<GameState[]>) => {
    const { value } = event.target;
    applyStates(
      (typeof value === 'string' ? value.split(',') : value) as GameState[]
    );
  };

  const visibleGames = useMemo(() => {
    return games
      .filter((game) => selectedStates.includes(game.state))
      .filter(
        (game) =>
          platformFilter === ALL_PLATFORMS || game.platform === platformFilter
      )
      .filter((game) => !pausedOnly || game.paused)
      .sort((a, b) => a.order - b.order);
  }, [games, selectedStates, platformFilter, pausedOnly]);

  const emptyMessage = pausedOnly
    ? 'No paused games.'
    : selectedStates.length === 1
      ? EMPTY_MESSAGES[selectedStates[0]]
      : 'No games match these filters.';

  const handleMenuClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>, game: GameListEntry) => {
      setAnchorEl(e.currentTarget);
      setSelectedGame(game);
    },
    []
  );

  const closeMenu = () => setAnchorEl(null);

  const handleAddGame = async (entry: NewGameListEntry) => {
    const duplicate = games.find(
      (game) =>
        game.title.toLowerCase() === entry.title.toLowerCase() &&
        game.platform === entry.platform
    );

    if (duplicate) {
      toast.error(`${entry.title} is already on your list for ${entry.platform}`);
      return;
    }

    const payload = {
      title: entry.title,
      year: entry.year,
      image: entry.image,
      platform: entry.platform,
      state: entry.state,
      paused: false,
      dateFinished: null,
      dateAdded: Timestamp.now(),
      order: topOrder(),
    };

    try {
      const created = await addDoc(collection(db, COLLECTION_NAME), payload);
      setGames((prev) => [...prev, { id: created.id, ...payload }]);

      // Make sure the new card is visible under the current filters.
      setSelectedStates((prev) => orderStates([...prev, entry.state]));
      setPausedOnly(false);
      if (platformFilter !== ALL_PLATFORMS && platformFilter !== entry.platform) {
        setPlatformFilter(entry.platform);
      }
      toast.success(`${entry.title} added`);

      lookupHltb(entry.title, entry.year)
        .then((hltb) => saveHltb(created.id, hltb))
        .catch((err) => console.error('HLTB lookup failed', entry.title, err));
    } catch (err) {
      console.error(err);
      toast.error('Failed to add game');
    }
  };

  const patchGame = async (
    game: GameListEntry,
    changes: Partial<GameListEntry>,
    successMessage?: string
  ) => {
    try {
      await updateDoc(doc(db, COLLECTION_NAME, game.id), changes);
      setGames((prev) =>
        prev.map((entry) =>
          entry.id === game.id ? { ...entry, ...changes } : entry
        )
      );
      if (successMessage) toast.success(successMessage);
    } catch (err) {
      console.error(err);
      toast.error('Failed to update game');
    }
  };

  const handleTogglePaused = useCallback((game: GameListEntry) => {
    patchGame(game, { paused: !game.paused });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMarkFinished = () => {
    if (!selectedGame) return;
    patchGame(
      selectedGame,
      {
        state: 'finished',
        paused: false,
        dateFinished: Timestamp.now(),
        order: topOrder(),
      },
      `${selectedGame.title} marked as completed`
    );
    closeMenu();
  };

  const handleMarkDropped = () => {
    if (!selectedGame) return;
    patchGame(
      selectedGame,
      {
        state: 'dropped',
        paused: false,
        dateFinished: null,
      },
      `${selectedGame.title} dropped`
    );
    closeMenu();
  };

  const handleFixHltbSave = async (hltb: HltbInfo) => {
    if (!selectedGame) return;
    const game = selectedGame;

    try {
      await saveHltb(game.id, hltb);
      toast.success(
        hltb.main
          ? `${game.title}: ${hltb.main}hrs main story`
          : `HLTB updated for ${game.title}`
      );
      setShowFixHltb(false);
    } catch (err) {
      console.error(err);
      toast.error('Failed to save HLTB time');
    }
  };

  const handleMoveToInProgress = () => {
    if (!selectedGame) return;
    patchGame(
      selectedGame,
      {
        state: 'inProgress',
        dateFinished: null,
        order: topOrder(),
      },
      `${selectedGame.title} moved to In Progress`
    );
    closeMenu();
  };

  const handleEditSave = (platform: string, state: GameState) => {
    if (!selectedGame) return;

    patchGame(
      selectedGame,
      {
        platform,
        state,
        paused: PAUSABLE_STATES.includes(state) ? selectedGame.paused : false,
        dateFinished:
          state === 'finished' ? selectedGame.dateFinished ?? null : null,
        ...(state !== selectedGame.state &&
          (state === 'finished' || state === 'inProgress') && {
            order: topOrder(),
          }),
      },
      `${selectedGame.title} updated`
    );

    setShowEdit(false);
  };

  const handleDelete = async () => {
    if (!selectedGame) return;

    try {
      await deleteDoc(doc(db, COLLECTION_NAME, selectedGame.id));
      setGames((prev) => prev.filter((game) => game.id !== selectedGame.id));
      toast.success(`${selectedGame.title} removed`);
    } catch (err) {
      console.error(err);
      toast.error('Failed to remove game');
    }

    setShowDelete(false);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Header />

      <Container maxWidth="xl" sx={{ py: 4, flex: 1 }}>
        <StandaloneBackButton />
        <PageHeading>Game List</PageHeading>

        <Box
          sx={{
            p: 2,
            mb: 3,
            ...filterPanel(theme),
            display: 'grid',
            alignItems: 'center',
            columnGap: 2,
            rowGap: 1.5,
            gridTemplateColumns: {
              xs: 'minmax(0, 1fr) auto auto',
              md: 'auto auto auto auto 1fr auto',
            },
            gridTemplateAreas: {
              xs: showPausedSwitch
                ? `"states states states" "platform clear add" "paused paused paused"`
                : `"states states states" "platform clear add"`,
              md: `"states platform paused clear . add"`,
            },
          }}
        >
          {isMobile ? (
            <FormControl size="small" sx={{ gridArea: 'states', minWidth: 0 }}>
              <InputLabel>States</InputLabel>
              <Select
                multiple
                value={selectedStates}
                label="States"
                onChange={handleStatesSelect}
                renderValue={(selected) =>
                  selected.map((state) => STATE_LABELS[state]).join(', ')
                }
              >
                {GAME_STATES.map((state) => (
                  <MenuItem key={state} value={state}>
                    <Checkbox
                      size="small"
                      checked={selectedStates.includes(state)}
                    />
                    <ListItemText primary={STATE_LABELS[state]} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <Box sx={{ gridArea: 'states', display: 'flex', gap: 1 }}>
              {GAME_STATES.map((state) => {
                const selected = selectedStates.includes(state);
                return (
                  <Chip
                    key={state}
                    label={STATE_LABELS[state]}
                    clickable
                    onClick={() => toggleState(state)}
                    variant={selected ? 'filled' : 'outlined'}
                    sx={
                      selected
                        ? {
                            backgroundImage: gradients.primary,
                            color: '#ffffff',
                            fontWeight: 700,
                            '&:hover': { filter: 'brightness(1.08)' },
                          }
                        : { borderColor: 'divider', color: 'text.primary' }
                    }
                  />
                );
              })}
            </Box>
          )}

          <FormControl
            size="small"
            sx={{ gridArea: 'platform', width: { xs: '100%', md: 150 }, minWidth: 0 }}
          >
            <InputLabel>Platform</InputLabel>
            <Select
              value={platformFilter}
              label="Platform"
              onChange={(e) => setPlatformFilter(e.target.value)}
            >
              <MenuItem value={ALL_PLATFORMS}>All platforms</MenuItem>
              {GAME_LIST_PLATFORMS.map((platform) => (
                <MenuItem key={platform} value={platform}>
                  {platform}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {showPausedSwitch && (
            <FormControlLabel
              sx={{ gridArea: 'paused', ml: 0 }}
              control={
                <Switch
                  checked={pausedOnly}
                  onChange={(e) => setPausedOnly(e.target.checked)}
                />
              }
              label="Paused only"
            />
          )}

          <Button
            variant="text"
            color="inherit"
            disabled={isDefaultFilters}
            onClick={clearFilters}
            sx={{ gridArea: 'clear', whiteSpace: 'nowrap' }}
          >
            Clear
          </Button>

          <Button
            variant="contained"
            onClick={() => setShowAdd(true)}
            sx={{ gridArea: 'add', whiteSpace: 'nowrap' }}
          >
            Add Game
          </Button>
        </Box>

        {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 300,
            }}
          >
            <CircularProgress color="primary" />
          </Box>
        ) : visibleGames.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography color="text.secondary">
              {emptyMessage}
            </Typography>
          </Box>
        ) : (
          <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns:
                    'repeat(auto-fill, minmax(280px, 1fr))',
                  gap: 3,
                  width: '100%',
                }}
              >
                {visibleGames.map((game) => (
                  <GameListCard
                    key={game.id}
                    game={game}
                    onMenuClick={handleMenuClick}
                    onTogglePaused={handleTogglePaused}
                  />
                ))}
              </Box>
        )}

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 3, textAlign: 'center' }}
        >
          Showing {visibleGames.length} game(s)
        </Typography>
      </Container>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}>
        <MenuItem
          onClick={() => {
            setShowEdit(true);
            closeMenu();
          }}
        >
          Edit
        </MenuItem>

        {selectedGame?.state !== 'finished' && (
          <MenuItem onClick={handleMarkFinished}>Mark as Completed</MenuItem>
        )}

        {selectedGame && PAUSABLE_STATES.includes(selectedGame.state) && (
          <MenuItem onClick={handleMarkDropped}>Mark as Dropped</MenuItem>
        )}

        {selectedGame?.state !== 'inProgress' && (
          <MenuItem onClick={handleMoveToInProgress}>
            Move to In Progress
          </MenuItem>
        )}

        <MenuItem
          onClick={() => {
            setShowFixHltb(true);
            closeMenu();
          }}
        >
          Fix HLTB…
        </MenuItem>

        <MenuItem
          sx={{ color: 'error.main' }}
          onClick={() => {
            setShowDelete(true);
            closeMenu();
          }}
        >
          Remove
        </MenuItem>
      </Menu>

      <AddGameListModal
        show={showAdd}
        onHide={() => setShowAdd(false)}
        onAddGame={handleAddGame}
      />

      <EditGameListModal
        show={showEdit}
        onHide={() => setShowEdit(false)}
        game={selectedGame}
        onSave={handleEditSave}
      />

      <FixHltbModal
        show={showFixHltb}
        onHide={() => setShowFixHltb(false)}
        game={selectedGame}
        onSave={handleFixHltbSave}
      />

      <ConfirmDeleteGameListModal
        show={showDelete}
        onHide={() => setShowDelete(false)}
        onConfirm={handleDelete}
        gameTitle={selectedGame?.title || ''}
      />

      <ToastContainer />
    </Box>
  );
};

export default GameList;
